import { all, put, select, takeLatest, call } from 'redux-saga/effects';
import { isNil } from 'ramda';

import {
  postProjectSettings as postProjectSettingsApi,
  fetchProjectSettings as fetchProjectSettingsApi,
} from 'spw-api';
import { APIResponse } from 'spw-redux/types';
import { snakeize } from 'spw-shared/utils';

import { handleError } from 'spw-redux/modules/UI/messages/actions';
import { camelCase, snakeCase } from 'lodash';

import {
  actions,
  SET_PROJECT_SETTINGS,
  FETCH_PROJECT_SETTINGS,
} from './actions';
import { currentProject } from '../projects/selectors';
import {
  defaultProjectSettings,
  IProjectSettings,
  RatioCalculationType,
} from './types';
import { selectProjectSettings } from './selector';
import {
  selectCurrentScenario,
  selectWorksheet,
} from '../calculators/selectors';
import { populateWorksheet } from '../calculators/actions';
import Worksheet, { PlainWorksheet } from '../../../models/Worksheet';
import { PlainProgramScenario } from '../../../models/Scenario';
import { IProject } from '../projects/types';

const { fetchProjectSettingsSucceeded } = actions;

function* setProjectSettings(
  action: ReturnType<typeof actions.setProjectSettings>
) {
  try {
    const {
      payload: { projectSettings },
    } = action;

    const project: IProject = yield select(currentProject);
    const settings = [];

    for (const key of Object.keys(projectSettings)) {
      if (!isNil(projectSettings[key as keyof IProjectSettings]))
        settings.push({
          key: snakeCase(key),
          value: snakeize(
            RatioCalculationType[projectSettings[key as keyof IProjectSettings]]
          ),
        });
    }

    const currentWorksheet: PlainWorksheet = yield select(selectWorksheet);
    if (currentWorksheet) {
      const scenario: PlainProgramScenario = yield select(
        selectCurrentScenario
      );
      const apiWorksheet = Worksheet.toAPIWorksheet(currentWorksheet);
      const newWorksheet = new Worksheet(
        apiWorksheet,
        scenario,
        projectSettings
      ).toPlainObject();
      yield put(populateWorksheet(newWorksheet));
    }

    yield call(postProjectSettingsApi, {
      settings,
      projectId: project?.id,
    });
    const oldSettings: IProjectSettings = yield select(selectProjectSettings);
    yield put(
      fetchProjectSettingsSucceeded({
        ...oldSettings,
        ...projectSettings,
      })
    );
  } catch (e) {
    yield put(handleError(e));
  }
}

function* fetchProjectSettings() {
  try {
    const project: IProject = yield select(currentProject);
    const response: APIResponse<typeof fetchProjectSettingsApi> = yield call(
      fetchProjectSettingsApi,
      project?.id
    );
    const settings: Record<string, unknown> = {};
    const projectSettings = response?.data?.projectSettings?.settings || [];

    for (const projectSetting of projectSettings) {
      settings[camelCase(projectSetting.key as string)] =
        RatioCalculationType[projectSetting.value];
    }
    const data = { ...defaultProjectSettings, ...settings };
    yield put(fetchProjectSettingsSucceeded(data));
  } catch (e) {
    yield put(handleError(e));
  }
}

function* saga() {
  yield all([
    takeLatest(SET_PROJECT_SETTINGS, setProjectSettings),
    takeLatest(FETCH_PROJECT_SETTINGS, fetchProjectSettings),
  ]);
}

export default saga;
